import React from "react";
import { TextField } from "@mui/material";
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import MonitorWeightRoundedIcon from '@mui/icons-material/MonitorWeightRounded';
import RestaurantRoundedIcon from '@mui/icons-material/RestaurantRounded';
import MedicalServicesRoundedIcon from '@mui/icons-material/MedicalServicesRounded';
import {
    Section,
    FieldRow,
    ChoiceField,
    MultiChoiceField,
    exerciseOptions,
    physiologicalOptions,
} from "./PatientFormPieces";

export const emptyPatientForm = {
    name: "",
    age: "",
    height: "",
    weight: "",
    dietaryPreference: "",
    alcohol: "",
    gender: "",
    foodAllergy: "",
    exerciseRegime: [],
    lifestyle: "",
    jobSpecifications: "",
    familyHistory: "",
    physiologicalConditions: [],
    otherPhysiologicalCondition: "",
    surgery: "",
    medications: "",
    supplements: "",
    eatingPattern: "",
    contactNumber: "",
    email: "",
    address: "",
};

// Shared by the full "Add Patient" and "Edit Patient" pages — same
// validation rules, same required fields. (The "Quick Add" short form is
// intentionally a different, smaller field set and has its own validator.)
export function validatePatientForm(patientData) {
    const formErrors = {};
    if (!patientData.name) formErrors.name = "Name is required";
    if (!patientData.age || isNaN(patientData.age) || patientData.age <= 0) formErrors.age = "Valid age is required";
    if (!patientData.height || isNaN(patientData.height) || patientData.height <= 0) formErrors.height = "Valid height is required";
    if (!patientData.weight || isNaN(patientData.weight) || patientData.weight <= 0) formErrors.weight = "Valid weight is required";
    if (!patientData.dietaryPreference) formErrors.dietaryPreference = "Dietary preference is required";
    if (!patientData.alcohol) formErrors.alcohol = "Alcohol consumption choice is required";
    if (!patientData.gender) formErrors.gender = "Gender is required";
    if (!patientData.foodAllergy) formErrors.foodAllergy = "Food Allergy is required field";
    if ((patientData.exerciseRegime || []).length === 0) formErrors.exerciseRegime = "Please select at least one Exercise Regime";
    if (!patientData.lifestyle) formErrors.lifestyle = "Lifestyle choice is required";
    if (!patientData.jobSpecifications) formErrors.jobSpecifications = "Job Specifications is required";
    if (!patientData.familyHistory) formErrors.familyHistory = "Family History is required";
    if ((patientData.physiologicalConditions || []).includes("Other") && !patientData.otherPhysiologicalCondition)
        formErrors.otherPhysiologicalCondition = "Please specify the other condition";
    if (!patientData.surgery) formErrors.surgery = "Surgery is required field";
    if (!patientData.medications) formErrors.medications = "Medications is required field";
    if (!patientData.supplements) formErrors.supplements = "Supplements is required field";
    if (!patientData.eatingPattern) formErrors.eatingPattern = "Please mention the eating pattern";
    if (!patientData.contactNumber) formErrors.contactNumber = "Contact number is required";
    if (!patientData.email) formErrors.email = "Email is required";
    if (!patientData.address) formErrors.address = "Address is required";

    return formErrors;
}

// The full field set (all four Sections) used by both the "Add Patient"
// (detailed) page and the "Edit Patient" page — identical layout either
// way, just pre-filled with existing data when editing.
export default function PatientFormFields({ patientData, errors, onInputChange, onChoiceChange, onMultiChoiceChange }) {
    return (
        <>
            <Section
                icon={<BadgeRoundedIcon />}
                title="Basic & Contact Information"
                subtitle="Who they are and how to reach them"
            >
                <TextField
                    fullWidth
                    name="name"
                    variant="outlined"
                    label="Patient Name"
                    value={patientData.name}
                    onChange={onInputChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                />

                <FieldRow columns={2}>
                    <TextField
                        fullWidth
                        name="email"
                        variant="outlined"
                        label="Email"
                        type="email"
                        value={patientData.email}
                        onChange={onInputChange}
                        error={!!errors.email}
                        helperText={errors.email}
                        required
                    />
                    <TextField
                        fullWidth
                        name="contactNumber"
                        variant="outlined"
                        label="Contact Number"
                        value={patientData.contactNumber}
                        onChange={onInputChange}
                        error={!!errors.contactNumber}
                        helperText={errors.contactNumber}
                        required
                    />
                </FieldRow>

                <TextField
                    fullWidth
                    name="address"
                    variant="outlined"
                    label="Address"
                    value={patientData.address}
                    onChange={onInputChange}
                    error={!!errors.address}
                    helperText={errors.address}
                    required
                />

                <ChoiceField
                    label="Gender"
                    error={errors.gender}
                    options={["Male", "Female", "Other"]}
                    value={patientData.gender}
                    onChange={onChoiceChange("gender")}
                />
            </Section>

            <Section
                icon={<MonitorWeightRoundedIcon />}
                title="Physical Measurements"
                subtitle="Basic vitals used to track progress"
            >
                <FieldRow columns={3}>
                    <TextField
                        fullWidth
                        name="age"
                        variant="outlined"
                        label="Age"
                        type="number"
                        value={patientData.age}
                        onChange={onInputChange}
                        error={!!errors.age}
                        helperText={errors.age}
                        required
                    />
                    <TextField
                        fullWidth
                        name="height"
                        variant="outlined"
                        label="Height"
                        type="number"
                        value={patientData.height}
                        onChange={onInputChange}
                        error={!!errors.height}
                        helperText={errors.height}
                        required
                    />
                    <TextField
                        fullWidth
                        name="weight"
                        variant="outlined"
                        label="Weight (kg)"
                        type="number"
                        value={patientData.weight}
                        onChange={onInputChange}
                        error={!!errors.weight}
                        helperText={errors.weight}
                        required
                    />
                </FieldRow>
            </Section>

            <Section
                icon={<RestaurantRoundedIcon />}
                title="Dietary & Lifestyle"
                subtitle="Habits that shape the diet plan"
            >
                <ChoiceField
                    label="Dietary Preference"
                    error={errors.dietaryPreference}
                    options={["Pure vegetarian", "Vegetarian + eggs", "Non vegetarian", "Vegan"]}
                    value={patientData.dietaryPreference}
                    onChange={onChoiceChange("dietaryPreference")}
                />

                <ChoiceField
                    label="Alcohol Consumption"
                    error={errors.alcohol}
                    options={["Yes", "No"]}
                    value={patientData.alcohol}
                    onChange={onChoiceChange("alcohol")}
                />

                <MultiChoiceField
                    label="Exercise Regime"
                    error={errors.exerciseRegime}
                    options={exerciseOptions}
                    value={patientData.exerciseRegime}
                    onChange={onMultiChoiceChange("exerciseRegime")}
                />

                <ChoiceField
                    label="Lifestyle"
                    error={errors.lifestyle}
                    options={["Active", "Sedentary"]}
                    value={patientData.lifestyle}
                    onChange={onChoiceChange("lifestyle")}
                />

                <TextField
                    fullWidth
                    name="eatingPattern"
                    variant="outlined"
                    label="Basic Eating Pattern, Food Habits and Timings"
                    value={patientData.eatingPattern}
                    multiline
                    minRows={2}
                    onChange={onInputChange}
                    error={!!errors.eatingPattern}
                    helperText={errors.eatingPattern}
                    required
                />

                <TextField
                    fullWidth
                    name="jobSpecifications"
                    variant="outlined"
                    label={`Job Specifications ( If none, type "No" )`}
                    value={patientData.jobSpecifications}
                    onChange={onInputChange}
                    error={!!errors.jobSpecifications}
                    helperText={errors.jobSpecifications}
                    required
                />
            </Section>

            <Section
                icon={<MedicalServicesRoundedIcon />}
                title="Medical History"
                subtitle="Conditions and history relevant to the diet plan"
            >
                <MultiChoiceField
                    label="Physiological Conditions"
                    error={errors.physiologicalConditions}
                    options={physiologicalOptions}
                    value={patientData.physiologicalConditions}
                    onChange={onMultiChoiceChange("physiologicalConditions")}
                />

                {patientData.physiologicalConditions.includes("Other") && (
                    <TextField
                        fullWidth
                        name="otherPhysiologicalCondition"
                        variant="outlined"
                        label="Please specify"
                        value={patientData.otherPhysiologicalCondition}
                        onChange={onInputChange}
                        error={!!errors.otherPhysiologicalCondition}
                        helperText={errors.otherPhysiologicalCondition}
                        required
                    />
                )}

                <TextField
                    fullWidth
                    name="foodAllergy"
                    variant="outlined"
                    label={`Food Allergy ( If none, type "No" )`}
                    value={patientData.foodAllergy}
                    onChange={onInputChange}
                    error={!!errors.foodAllergy}
                    helperText={errors.foodAllergy}
                    required
                />
                <TextField
                    fullWidth
                    name="familyHistory"
                    variant="outlined"
                    label={`Family History of Lifestyle Disease ( If none, type "No" )`}
                    value={patientData.familyHistory}
                    onChange={onInputChange}
                    error={!!errors.familyHistory}
                    helperText={errors.familyHistory}
                    required
                />
                <TextField
                    fullWidth
                    name="surgery"
                    variant="outlined"
                    label={`Any Surgery ( If none, type "No" )`}
                    value={patientData.surgery}
                    onChange={onInputChange}
                    error={!!errors.surgery}
                    helperText={errors.surgery}
                    required
                />
                <TextField
                    fullWidth
                    name="medications"
                    variant="outlined"
                    label={`Medications ( If none, type "No" )`}
                    value={patientData.medications}
                    onChange={onInputChange}
                    error={!!errors.medications}
                    helperText={errors.medications}
                    required
                />
                <TextField
                    fullWidth
                    name="supplements"
                    variant="outlined"
                    label={`Supplements ( If none, type "No" )`}
                    value={patientData.supplements}
                    onChange={onInputChange}
                    error={!!errors.supplements}
                    helperText={errors.supplements}
                    required
                />
            </Section>
        </>
    );
}
